<template>
    <div class="dashboard-wrapper">
        <div class="dashboard-row">
            <div class="dashboard-card overall-rating">
                <div class="overall-rating-content">
                    <p class="dashboard-title">Overall Rating</p>
                    <div class="main-detail">
                        <i class="bx bxs-star"></i>
                        <p>{{ formatDecimal(this.satisfactionSummaryData.average_score) }}</p>
                    </div>
                    <p>From</p>
                    <div class="overall-rating-footer">
                        <i class="bx bx-group"></i>
                        <p><span class="overall-rating-number">{{ this.satisfactionSummaryData.surveyed }} </span>Respondents</p>
                    </div>
                </div>
            </div>
            <div class="dashboard-card all-time-rating">
                <div class="all-time-rating">
                    <div class="header-widget">
                        <p>All Time Rating</p>
                    </div>
                        <NoResultsFound v-if="satisfactionSummaryData.surveyed <= 0" class="no-result-content"/>
                        <div v-else class="all-time-rating-content">
                          <div class="chart-content">
                            <GChart
                            id="bot-performance-chart"
                            class="g-chart"
                            type="PieChart"
                            :data="ratingChartData"
                            :options="chartOptions"
                            :events="chartEvents"
                            ></GChart>
                          </div>
                          <div v-if="surveyedNameToSatisfactionSummaryDetailMap.summaryStat !== null" class="rating-content">
                            <div v-for="(rating, index) in surveyedNameToSatisfactionSummaryDetailMap" :key="`rating`+index">
                              <div class="rating-detail">
                                <div class="rating-dot-color" v-bind:style="{color: rating.colorCode}">
                                  <i class='bx bxs-circle'></i>
                                </div>
                                <p>{{ rating.label }}</p>
                                <div class="topic-progress-bar">
                                  <div class="progress striped" v-bind:style="{ width: ratingPercentages( rating.summaryStat) + '%' }"></div>
                                </div>
                                <div class="rating-number">
                                  <p>{{ rating.summaryStat | number }}<span>&nbsp;</span>({{ratingPercentages(rating.summaryStat)}}%)</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                </div>
            </div>
        </div>
        <div class="dashboard-row">
          <div class="dashboard-card customer-feedbacks">
            <div class="header-widget">
                <p>Customer feedbacks</p>
            </div>
            <CustomerFeedbacksWidget v-if="Array.isArray(customerFeedbackData.content)" v-bind:customerFeedbackData="customerFeedbackData" @selected-page="onSelectedPage($event)"/>
          </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { GChart } from 'vue-google-charts';
import CustomerFeedbacksWidget from '../DashboardWidgets/CustomerFeedbacksWidget.vue';
import alice from '../../alice-api';
import NoResultsFound from '../NoResultsFound.vue';

export default {
  name: 'customer-satisdaction-dashboard',
  components: {
    CustomerFeedbacksWidget,
    GChart,
    NoResultsFound
  },
  props: {
    criteria: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      currentPageFilter: 0,
      customerFeedbackData: {},
      satisfactionSummaryData: {},
      surveyedNameToSatisfactionSummaryDetailMap: {
        colorCode: '',
        summaryStat: null,
        name: '',
      },
      chartOptions: {
        height: 200,
        chartArea: {
          top: 50,
        },
        colors: ['#EB0B0B', '#F8B300', '#F7DD00', '#89D421', '#44BC37'],
        legend: 'none',
        pieHole: 0.6,
        pieStartAngle: 270,
        pieSliceText: 'none',
        title: 'Fallback Rate',
        tooltip: {
          text: 'value',
        },
        slices: {
          5: {
            color: 'transparent',
          },
        },
        theme: 'maximized',
      },
      chartEvents: {
        ready: () => {
          // set width of label 0 - 100
          try {
            const chartWidth = document
              .querySelector('#bot-performance-chart g')
              .getBoundingClientRect().width;
            document.querySelector('.float-unit-content').style.width = `${chartWidth}px`;
          } catch (e) {
            // console.log('Cannot get chart width');
          }
          if (document.querySelector('#bot-performance-chart text') && document.querySelector('#bot-performance-chart text').innerHTML === 'No data') {
            document.querySelector('#bot-performance-chart text').style.opacity = '0.5';
          }
        },
      },
    };
  },
  async mounted() {
    this.getSatisfaction(this.criteria);
  },

  methods: {
    ...mapActions({
      loadSatisfactionWithFilter: 'satisfaction/loadSatisfactionWithFilter',
    }),
    async getSatisfaction(criteria) {
      let filter = [];
      if (criteria.channel) {
        filter = this.prepareFilterData(criteria.channel);
      }
      this.customerFeedbackData = await this.loadSatisfactionWithFilter({
        searchBody: {
          filter,
          dateFilter: {
            from: this.criteria.time.sinceDate,
            to: this.criteria.time.untilDate,
          },
          onlySurvey: true,
          size: 10,
          page: this.currentPageFilter,
          sortBy: 'created_at',
          sortDirection: 'DESC',
        },
      });
      this.satisfactionSummaryData = await alice.getFormSummaryWithFilter({
        filter,
        dateFilter: {
          from: this.criteria.time.sinceDate,
          to: this.criteria.time.untilDate,
        },
        sortBy: 'created_at',
        sortDirection: 'ASC',
        onlySurvey: true,
      });
      this.satisfactionSummaryData = this.satisfactionSummaryData.data.data;
      this.mapSurveyedStatsToDisplay();
    },
    mapSurveyedStatsToDisplay() {
      this.surveyedNameToSatisfactionSummaryDetailMap = {};
      // eslint-disable-next-line guard-for-in
      for (const rating in this.satisfactionSummaryData.surveyed_stat) {
        if (this.satisfactionSummaryData.surveyed_stat[rating] === undefined) {
          // eslint-disable-next-line no-continue
          continue;
        }
        switch (rating) {
          case 'very_satisfied':
            this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
              colorCode: '#44BC37',
              label: 'Extremely'
            };
            break;
          case 'satisfied':
            this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
              colorCode: '#89D421',
              label: 'Good'
            };
            break;
          case 'ok':
            this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
              colorCode: '#F7DD00',
              label: 'Fair'
            };
            break;
          case 'dissatisfied':
            this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
              colorCode: '#F8B300',
              label: 'Poor'
            };
            break;
          case 'very_dissatisfied':
            this.surveyedNameToSatisfactionSummaryDetailMap[rating] = {
              colorCode: '#EB0B0B',
              label: 'Very Poor'
            };
            break;
          default:
            // eslint-disable-next-line no-continue
            continue;
        }
        this.surveyedNameToSatisfactionSummaryDetailMap[rating].summaryStat = this.satisfactionSummaryData.surveyed_stat[rating];
      }
    },
    prepareFilterData(channel) {
      return [{
        field: 'channel',
        value: [channel]
      }
      ];
    },
    async onSelectedPage(page) {
      this.currentPageFilter = page;
      await this.getSatisfaction(this.criteria);
    },
    async onChangeCriteria() {
      this.currentPageFilter = 0;
      await this.getSatisfaction(this.criteria);
    },
    formatDecimal(num) {
      if (!num) {
        return 0;
      }
      return Number(num.toFixed(2));
    },
    //  Calculate percentage for each rating
    ratingPercentages(value) {
      if (this.satisfactionSummaryData.surveyed === 0) {
        return 0;
      }
      return ((value / this.satisfactionSummaryData.surveyed) * 100).toFixed(2);
    }
  },
  computed: {
    ratingChartData() {
      const chartData = [['Rating', 'score']];
      if (this.satisfactionSummaryData.surveyed > 0) {
        const ratingLabels = {
          very_dissatisfied: 'Very Poor',
          dissatisfied: 'Poor',
          ok: 'Fair',
          satisfied: 'Good',
          very_satisfied: 'Extremely',
        };
        const ratingOrder = ['very_dissatisfied', 'dissatisfied', 'ok', 'satisfied', 'very_satisfied'];

        const surveyedStatData = ratingOrder.map((rating) => [
          ratingLabels[rating],
          this.satisfactionSummaryData.surveyed_stat[rating]
        ]);
        surveyedStatData.push([null, this.satisfactionSummaryData.surveyed]);
        return chartData.concat(surveyedStatData);
      }
      return chartData.concat([[null, 0]]);
    },
  },
  watch: {
    criteria: {
      handler: 'onChangeCriteria',
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
.dashboard-wrapper{
    .dashboard-row{
        display: grid;
        grid-template-columns: repeat(7, minmax(0, 1fr));
        gap: 24px;
        padding-bottom: 24px;
        min-height: 140px;
        .dashboard-card{
            background-color: #FFFFFF;
            border-radius: 16px;
            padding: 44px 24px ;
            font-weight: 500;
            .dashboard-title{
            font-size: 20px;
            font-weight: 500;
            line-height: 24px;
            color: #4B5178;
            }
        }
        .header-widget{
            padding: 16px 16px 12px 16px;
            border-bottom: 1px solid #E9EBEC;
            font-size: 16px;
            font-weight: 500;
            line-height: 19.2px;
            color: #111B49;
            display: flex;
            justify-content: space-between;
            p {
                margin-bottom: 0px
            }
        }
        .dashboard-card.overall-rating{
            grid-column: span 2 / span 2;
            display: flex;
            justify-content: center;
            align-items: center;
            .overall-rating-content{
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                .main-detail{
                    display: flex;
                    justify-content: start;
                    align-items: center;
                    font-size: 40px;
                    font-weight: 500;
                    line-height: 48px;
                    color:#111B49;
                    padding: 20px 0px 24px 0px;
                    i {
                    color: #FCD14E;
                    font-size: 24px;
                    padding-right: 6px;
                    }
                    p{
                        margin-bottom: 0px;
                    }
                }
                .overall-rating-footer{
                    display: flex;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 21px;
                    color: #646A8C;
                    p{
                        margin-bottom: 0px;
                    }
                    i{
                        color: #59CBE8;
                        font-size: 24px;
                    }
                    .overall-rating-number{
                        font-weight: 500;
                    }
                }
            }
        }
        .dashboard-card.all-time-rating{
            grid-column: span 5 / span 5;
            padding: 0px;
            .all-time-rating-content{
              display: grid;
              grid-template-columns: repeat(7, minmax(0, 1fr));
              .chart-content{
                grid-column: span 3 / span 3;
              }
              .rating-content{
                grid-column: span 4 / span 4;
                display: flex;
                flex-direction: column;
                flex-grow: 1;
                padding: 38px;
                padding-left: 0px;
                .rating-detail{
                  display: flex;
                  align-items: center;
                  flex-grow: 1;
                  p{
                    margin-bottom: 0px;
                    min-width: 63px;
                  }
                  .rating-dot-color{
                    font-size: 8px;
                    padding-right: 8px;
                  }
                  .rating-number{
                    p{
                      width: 80px;
                    }
                  }
                }
              }
            }
        }
        .dashboard-card.customer-feedbacks{
            grid-column: span 7 / span 7;
            padding: 0px;

        }
    }
    .topic-progress-bar {
      width: 100%;
      background-color: #e0e0e0;
      border-radius: 8px;
      overflow: hidden;
      height: 6px;
      margin-right: 20px;
      margin-left: 20px;
    }
    .progress {
      height: 100%;
      background-color: #3371FF;
      width: 0;
      // transition: width 0.3s ease;
      border-radius: 8px;
    }
    .progress.striped::before {
      content: '';/* Pseudo-element for stripes */
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.1) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.1) 75%,
        transparent 75%,
        transparent 100%
      );
      background-size: 8px 8px;/* Size of the stripe pattern */
    }
    .no-result-content{
      margin-top: 40px;
      margin-bottom: 40px;
    }
}
</style>
